import React, { useMemo, useState, useCallback } from 'react';
import {
  FlatList,
  ScrollView,
  StyleSheet,
  Text,
  View,
  LayoutChangeEvent,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { DataTable, IconButton } from 'react-native-paper';
import { useTheme, GTFonts } from '../../theme';
import { useAppSelector } from '../../redux/helper';
import { selectEnableTableRotation } from '../../redux/SettingsSlice';
import { s, fs, iconSize, v } from '../../utils/libs';
import AppSkeleton from '../Reusables/AppSkeleton';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export type AppDataTableColumn = {
  title: string;
  width: number;
  align?: 'left' | 'center' | 'right';
  wrap?: boolean; // default true
};

export type AppDataTablePaginationProps = {
  page: number;
  numberOfPages: number;
  onPageChange: (page: number) => void;
  label: string;
  showFastPaginationControls?: boolean;
  numberOfItemsPerPageList?: number[];
  numberOfItemsPerPage?: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  selectPageDropdownLabel?: string;
};

type AppDataTableProps = {
  columns: AppDataTableColumn[];
  data: Array<Array<React.ReactNode>>;
  loading?: boolean;
  onRowPress?: (rowIndex: number) => void;
  keyExtractor?: (row: Array<React.ReactNode>, index: number) => string;
  maxBodyHeight?: number; // normalized height (optional). If omitted, body fills available space
  footerPagination?: AppDataTablePaginationProps | null;
  emptyMessage?: string;
  // Controls visibility of the rotate (wide-view) toggle button for this table instance
  showRotateToggle?: boolean; // default true
  // Optional toolbar rendered above the table (e.g., searchbar, filters)
  toolbar?: React.ReactNode;
  // If true, hide the toolbar when the table is in rotated (wide) mode
  hideToolbarWhenRotated?: boolean; // default false
};

const AppDataTable: React.FC<AppDataTableProps> = ({
  columns,
  data,
  loading = false,
  onRowPress,
  keyExtractor,
  maxBodyHeight,
  footerPagination,
  emptyMessage,
  showRotateToggle = true,
  toolbar,
  hideToolbarWhenRotated = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(
    () => createStyles(theme, maxBodyHeight),
    [theme, maxBodyHeight],
  );
  const enableRotation = useAppSelector(selectEnableTableRotation);
  const { width: windowWidth } = useWindowDimensions();
  // Hide rotate toggle on web for larger screens (1024px+)
  const isWebLargeScreen = Platform.OS === 'web' && windowWidth >= 1024;

  const totalTableWidth = columns.reduce((sum, c) => sum + (c.width || 0), 0);

  // Wide (rotated) mode state + animation
  const [isRotated, setIsRotated] = useState(false);
  const rotationProgress = useSharedValue(0); // 0 = normal, 1 = rotated
  const containerW = useSharedValue(0);
  const containerH = useSharedValue(0);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });

  const onContainerLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { width, height } = e.nativeEvent.layout;
      containerW.value = width;
      containerH.value = height;
      setContainerDimensions({ width, height });
    },
    [containerW, containerH],
  );

  const toggleRotate = useCallback(() => {
    const next = !isRotated;
    setIsRotated(next);
    rotationProgress.value = withTiming(next ? 1 : 0, {
      duration: 260,
      easing: Easing.out(Easing.cubic),
    });
  }, [isRotated, rotationProgress]);

  const animatedTableStyle = useAnimatedStyle(() => {
    const rotate = `${-90 * rotationProgress.value}deg`;
    return {
      transform: [{ rotateZ: rotate }],
    };
  });

  return (
    <View style={styles.tableContainer} onLayout={onContainerLayout}>
      <Animated.View
        style={[
          styles.rotatableContainer,
          animatedTableStyle,
          isRotated
            ? {
                width: containerDimensions.height,
                height: containerDimensions.width,
                alignSelf: 'flex-start',
                paddingBottom: 200,
              }
            : undefined,
        ]}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          style={{ flex: 1 }}
          contentContainerStyle={{ minWidth: totalTableWidth }}>
          <DataTable>
            {toolbar && !(hideToolbarWhenRotated && isRotated) ? (
              <View
                style={[styles.toolbarContainer, { width: totalTableWidth }]}>
                {toolbar}
              </View>
            ) : null}
            <DataTable.Header style={[styles.head, { width: totalTableWidth }]}>
              {columns.map((col, i) => {
                const align = col.align || 'center';
                return (
                  <DataTable.Title
                    key={String(col.title) + i}
                    numberOfLines={2}
                    style={{
                      width: col.width,
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: col.width,
                      justifyContent:
                        align === 'right'
                          ? 'flex-end'
                          : align === 'left'
                            ? 'flex-start'
                            : 'center',
                    }}
                    textStyle={[styles.headText, { textAlign: align }]}>
                    {col.title}
                  </DataTable.Title>
                );
              })}
            </DataTable.Header>
            {loading ? (
              <AppSkeleton
                mode="table"
                columnWidths={columns.map(c => c.width)}
                numRows={10}
              />
            ) : data.length === 0 ? (
              <View style={styles.emptyWrap}>
                <Text style={styles.emptyText}>
                  {emptyMessage || 'No data in this time period'}
                </Text>
              </View>
            ) : (
              <FlatList
                data={data}
                keyExtractor={(row, i) =>
                  keyExtractor ? keyExtractor(row, i) : String(i)
                }
                nestedScrollEnabled
                showsVerticalScrollIndicator
                style={styles.dataWrapper}
                contentContainerStyle={styles.bodyContentContainer}
                renderItem={({ item: row, index }) => (
                  <DataTable.Row
                    onPress={onRowPress ? () => onRowPress(index) : undefined}
                    style={[
                      styles.row,
                      { width: totalTableWidth },
                      index % 2 === 0 ? styles.rowEven : styles.rowOdd,
                    ]}>
                    {row.map((cell, i) => {
                      const col = columns[i];
                      const align = col?.align || 'center';
                      const wrap = col?.wrap !== false;
                      const isPrimitive =
                        typeof cell === 'string' || typeof cell === 'number';
                      return (
                        <DataTable.Cell
                          key={String(i)}
                          style={[
                            {
                              width: col.width,
                              flexGrow: 0,
                              flexShrink: 0,
                              flexBasis: col.width,
                              justifyContent:
                                align === 'right'
                                  ? 'flex-end'
                                  : align === 'left'
                                    ? 'flex-start'
                                    : 'center',
                              alignItems: 'center',
                            },
                          ]}>
                          {isPrimitive ? (
                            <Text
                              style={[
                                styles.rowText,
                                { textAlign: align },
                                wrap ? styles.wrapText : undefined,
                              ]}>
                              {String(cell)}
                            </Text>
                          ) : (
                            (cell as React.ReactElement)
                          )}
                        </DataTable.Cell>
                      );
                    })}
                  </DataTable.Row>
                )}
              />
            )}
          </DataTable>
        </ScrollView>

        {footerPagination ? (
          <DataTable.Pagination
            page={footerPagination.page}
            numberOfPages={footerPagination.numberOfPages}
            onPageChange={footerPagination.onPageChange}
            label={footerPagination.label}
            showFastPaginationControls={
              footerPagination.showFastPaginationControls
            }
            numberOfItemsPerPageList={footerPagination.numberOfItemsPerPageList}
            numberOfItemsPerPage={footerPagination.numberOfItemsPerPage}
            onItemsPerPageChange={footerPagination.onItemsPerPageChange}
            selectPageDropdownLabel={footerPagination.selectPageDropdownLabel}
          />
        ) : null}
      </Animated.View>

      {/* Floating rotate toggle (non-blocking) - hidden on web large screens */}
      {enableRotation && showRotateToggle && !isWebLargeScreen ? (
        <View pointerEvents="box-none" style={styles.rotateButtonWrap}>
          <IconButton
            icon={
              isRotated ? 'phone-rotate-portrait' : 'phone-rotate-landscape'
            }
            selected={isRotated}
            size={iconSize(22)}
            style={styles.rotateButton}
            containerColor={theme.colors.surfaceSecondary}
            iconColor={theme.colors.text}
            onPress={toggleRotate}
            accessibilityLabel={
              isRotated ? 'Exit wide view' : 'Enter wide view'
            }
          />
        </View>
      ) : null}
    </View>
  );
};

export default AppDataTable;

const createStyles = (theme: any, maxBodyHeight?: number) =>
  StyleSheet.create({
    tableContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      overflow: 'hidden',
      borderRadius: theme.borderRadius.md,
    },
    toolbarContainer: {
      paddingLeft: s(40),
      paddingRight: s(10),
      paddingTop: v(10),
      paddingBottom: v(8),
      backgroundColor: theme.colors.surface,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    rotatableContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    head: {
      minHeight: v(48),
      backgroundColor: theme.colors.tableHeader,
      paddingHorizontal: 0,
    },
    headText: {
      margin: s(6),
      fontSize: fs(15),
      lineHeight: fs(17),
      fontFamily: GTFonts.semibold,
      color: theme.colors.tableHeaderText,
      flex: 1,
    },
    dataWrapper: {
      // Let body fill available vertical space; cap only if a max is provided
      flexGrow: 1,
      ...(typeof maxBodyHeight === 'number'
        ? { maxHeight: maxBodyHeight }
        : {}),
    },
    bodyContentContainer: {
      paddingBottom: v(72), // keep last row above pagination & fab
    },
    emptyWrap: {
      // When empty, keep a reasonable height. If max provided, respect it.
      minHeight:
        typeof maxBodyHeight === 'number'
          ? Math.min(maxBodyHeight, v(240))
          : v(240),
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(40),
    },
    emptyText: {
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      fontFamily: GTFonts.regular,
    },
    row: {
      minHeight: v(Platform.OS === 'web' ? 56 : 48),
      backgroundColor: theme.colors.surface,
      paddingHorizontal: 0,
    },
    rowEven: {
      backgroundColor: theme.colors.surface,
    },
    rowOdd: {
      backgroundColor: theme.colors.tableSecondary,
    },
    rowText: {
      margin: s(2),
      fontSize: fs(14),
      lineHeight: fs(18),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
    },
    wrapText: {
      flexShrink: 1,
      flexWrap: 'wrap',
    },
    rotateButtonWrap: {
      position: 'absolute',
      left: s(8),
      top: v(8),
    },
    rotateButton: {
      margin: 0,
    },
  });
